import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy,  Component, DestroyRef, ElementRef, effect, HostListener, inject, DOCUMENT, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@core/services/alert.service';
import { SidebarService } from '@core/services/sidebar.service';
import { SubscriptionFeaturesService } from '@core/services/subscription-features.service';
import { MenuItem } from '@models/menuItem.interface';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { normalizeUrl } from '@core/utils/util';
import { CompanyContextService } from '@core/context/company-context.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { ensureAdminIngredientsMenuItem } from '@core/utils/menu-business-visibility';

const ROUTE_FEATURES: Record<string, string> = { '/users': 'users.manage',
  '/role': 'users.roles_permissions',
  '/permissions': 'users.roles_permissions',
  '/devices': 'devices.module',
  '/customers': 'contacts.customers',
  '/suppliers': 'contacts.suppliers',
  '/promotions-rewards': 'marketing.rewards',
  '/rewards': 'marketing.rewards',
  '/memberships': 'marketing.memberships',
  '/offers': 'marketing.offers',
  '/finances/expenses': 'finance.expenses',
  '/finances/expense-types': 'finance.expenses',
  '/my-tasks': 'employees.attendance',
  '/my-attendance': 'employees.attendance',
  '/admin/task-manager': 'employees.attendance',
  '/admin/attendance': 'employees.attendance',
  '/inventory-movements': 'inventory.movements',
  '/ingredients': 'ingredients.module',
  '/returns': 'sales.returns',
  '/profit-loss': 'finance.profit_loss' };

const ALWAYS_HIDDEN_PATHS = new Set(['/menu']);
const STARTER_HIDDEN_PATHS = new Set(['/users', '/devices', '/contacts', '/customers', '/suppliers', '/finances', '/finances/expenses', '/finances/expense-types', '/branches']);

@Component({ selector: 'app-sidebar',
    imports: [CommonModule],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush })
export class SidebarComponent { @ViewChild('sidebarPanel')
  private readonly sidebarPanel?: ElementRef<HTMLElement>;

    readonly tree = signal<MenuItem[]>([]);
    readonly visibleTree = signal<MenuItem[]>([]);
    readonly open = signal(true);
    readonly mobileOpen = signal(false);
    readonly isMobileViewport = signal(false);
  activePath?: string;
  expanded = new Set<number>();
  readonly quickSalePath = '/sale';
  private bodyOverflow = '';
  private isBodyScrollLocked = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly alertService = inject(AlertService);
  private readonly subscriptionFeaturesService = inject(SubscriptionFeaturesService);
  readonly sidebarService = inject(SidebarService);
  readonly router = inject(Router);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly tenantSessionService = inject(TenantSessionService);
    private readonly currentBusinessName = signal(this.companyContextService.value?.businessObject?.name ?? null);

  constructor() { effect(() => { this.subscriptionFeaturesService.currentPlanName();
      this.subscriptionFeaturesService.entitlements();
      this.subscriptionFeaturesService.isLoading();
      this.visibleTree.set(this.computeVisibleTree()); });

    this.subscriptionFeaturesService.load().subscribe({ error: () => undefined });

    this.sidebarService.isExpanded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => { this.open.set(v); });
    this.sidebarService.isMobileViewport$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => { this.isMobileViewport.set(v);
        this.syncBodyScrollLock(); });
    this.sidebarService.isMobileOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => { if (!v) { this.releaseFocusFromHiddenSidebar(); }
        this.mobileOpen.set(v);
        this.syncBodyScrollLock(); });
    this.sidebarService.tree$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tree) => { this.tree.set(tree ?? []);
        this.visibleTree.set(this.computeVisibleTree());
        this.syncActiveFromUrl(normalizeUrl(this.router.url)); });

    this.companyContextService.company$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => { this.currentBusinessName.set(company?.businessObject?.name ?? null);
        this.visibleTree.set(this.computeVisibleTree()); });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((e: any) => { this.syncActiveFromUrl(normalizeUrl(e.url));
        if (this.isMobileViewport()) { this.closeMobile(); } });
    this.destroyRef.onDestroy(() => this.unlockBodyScroll()); }

  // --- helpers ---
  hasChildren = (n: MenuItem) => !!n.childrenItems?.length;
  isExpanded = (id: number) => this.expanded.has(id);
  isActive = (n: MenuItem) =>
    !!n.path && this.activePath === normalizeUrl(n.path);
  isLocked = (n: MenuItem) => this.isMenuItemLocked(n);
  isComingSoon = (n: MenuItem) => this.isMenuItemComingSoon(n);
  get showExpandedLayout(): boolean { return this.isMobileViewport() || this.open(); }
  get isQuickSaleActive(): boolean { return this.activePath === normalizeUrl(this.quickSalePath); }

  toggle() { if (this.isMobileViewport()) { this.closeMobile();
      return; }

    this.open.update((value) => !value);
    if (!this.open()) { this.expanded = new Set<number>();
      this.sidebarService.collapse();
      return; }

    this.sidebarService.expand(); }

  toggleNode(n: MenuItem, fromClick = true) { if (!this.showExpandedLayout) this.toggle();
    if (this.expanded.has(n.id)) { this.expanded = new Set(this.sidebarService.getAncestors(n.id)); } else { this.expandBranch(n); }
    if (fromClick && !n.path) this.activePath = undefined; }

  onClick(n: MenuItem) { if (this.isMenuItemLocked(n)) { this.alertService.open({ type: 'warning',
        message: this.getLockedMessage(n) });
      return; }

    if (this.isMenuItemComingSoon(n)) { this.alertService.open({ type: 'info',
        message: 'Esta funcionalidad estará disponible próximamente.' });
      return; }

    if (n.path && this.isActive(n)) { this.navigate(n.path);
      return; }

    if (!this.showExpandedLayout) { this.open.set(true);
      this.sidebarService.expand();
      if (this.hasChildren(n)) { this.expandBranch(n); }
      return; }
    if (this.hasChildren(n)) { this.toggleNode(n, true);
      return; }
    if (n.path) this.navigate(n.path); }

  navigate(path: string) { const url = normalizeUrl(path);
    this.setActive(url);
    this.router.navigateByUrl(path, { onSameUrlNavigation: 'reload' }); }

  closeMobile() { this.sidebarService.closeMobile(); }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void { if (this.isMobileViewport() && this.mobileOpen()) { this.closeMobile(); } }

  private setActive(url: string) { this.activePath = url;
    this.setExpandedByUrl(url); }

  private setExpandedByUrl(url: string) { const nextExpanded = new Set<number>();
    const id = this.sidebarService.getIdByPath(url);
    if (id === undefined) { this.expanded = nextExpanded;
      return; }
    for (const a of this.sidebarService.getAncestors(id)) { nextExpanded.add(a); }
    this.expanded = nextExpanded; }

  private syncActiveFromUrl(url: string) { this.activePath = url;
    this.setExpandedByUrl(url); }

  private expandBranch(node: MenuItem): void { const nextExpanded = new Set<number>(this.sidebarService.getAncestors(node.id));
    nextExpanded.add(node.id);
    this.expanded = nextExpanded; }

  private computeVisibleTree(): MenuItem[] { return ensureAdminIngredientsMenuItem(
      this.filterTree(this.tree()),
      this.currentBusinessName(),
      this.isCurrentUserAdministrator(),
      this.subscriptionFeaturesService.isEnabled('ingredients.module'),
    ); }

  getItemFeature(item: MenuItem): string | null { if (!item.path) { return null; }

    return ROUTE_FEATURES[normalizeUrl(item.path)] ?? null; }

  getItemBadgeLabel(item: MenuItem): string | null { if (this.isMenuItemComingSoon(item)) { return 'Próximamente'; }

    if (this.isMenuItemLocked(item)) { return '🔒'; }

    return null; }

  getItemTitle(item: MenuItem): string | null { if (this.isMenuItemComingSoon(item)) { return 'Próximamente'; }

    if (this.isMenuItemLocked(item)) { return this.getLockedMessage(item); }

    return !this.showExpandedLayout ? item.name : null; }

  inActiveBranch(n: MenuItem): boolean { const id = this.sidebarService.getIdByPath(this.activePath ?? '');
    if (id === undefined) return false;
    return this.sidebarService.getAncestors(id).includes(n.id); }

  private filterTree(nodes: MenuItem[]): MenuItem[] { return nodes.reduce<MenuItem[]>((accumulator, node) => { const childrenItems = Array.isArray(node.childrenItems)
        ? this.filterTree(node.childrenItems)
        : [];
      const shouldHideNode =
        this.isMenuItemHidden(node) ||
        this.isMenuItemLocked(node) ||
        this.isMenuItemComingSoon(node);

      if (shouldHideNode) { if (childrenItems.length) { accumulator.push({ ...node,
            path: '',
            childrenItems }); }

        return accumulator; }

      if (!node.path && Array.isArray(node.childrenItems) && node.childrenItems.length > 0 && !childrenItems.length) { return accumulator; }

      accumulator.push({ ...node,
        childrenItems });

      return accumulator; }, []); }

  private isMenuItemHidden(item: MenuItem): boolean { const normalizedPath = normalizeUrl(item.path ?? '');

    if (ALWAYS_HIDDEN_PATHS.has(normalizedPath)) { return true; }

    if (this.isStarterPlan() && STARTER_HIDDEN_PATHS.has(normalizedPath)) { return true; }

    return false; }

  private isMenuItemLocked(item: MenuItem): boolean { const featureKey = this.getItemFeature(item);

    if (!featureKey || this.subscriptionFeaturesService.isComingSoon(featureKey)) { return false; }

    return !this.subscriptionFeaturesService.isEnabled(featureKey); }

  private isMenuItemComingSoon(item: MenuItem): boolean { const featureKey = this.getItemFeature(item);
    return featureKey ? this.subscriptionFeaturesService.isComingSoon(featureKey) : false; }

  private getLockedMessage(item: MenuItem): string { const featureKey = this.getItemFeature(item);
    if (!featureKey) { return 'Actualiza tu plan para acceder.'; }

    const nextPlanName = this.subscriptionFeaturesService.getNextPlanName(featureKey);
    return nextPlanName
      ? `Disponible en plan ${nextPlanName}. Actualiza tu plan para acceder.`
      : 'Actualiza tu plan para acceder.'; }

  private isStarterPlan(): boolean { const name = (this.subscriptionFeaturesService.currentPlanName() ?? '').trim().toLowerCase();
    return name === 'inicial' || name === 'starter'; }

  private isCurrentUserAdministrator(): boolean { return (this.tenantSessionService.getCurrentUser()?.rolesObject ?? []).some(
      (role) => (role.role_key ?? role.catalogRoleObject?.role_key) === 'ADMIN',
    ); }

  private syncBodyScrollLock(): void { const shouldLock = this.isMobileViewport() && this.mobileOpen();
    if (shouldLock === this.isBodyScrollLocked) { return; }

    if (shouldLock) { this.blurActiveElement();
      this.bodyOverflow = this.document.body.style.overflow;
      this.document.body.style.overflow = 'hidden';
      this.isBodyScrollLocked = true;
      return; }

    this.unlockBodyScroll(); }

  private unlockBodyScroll(): void { if (!this.isBodyScrollLocked) { return; }

    this.document.body.style.overflow = this.bodyOverflow;
    this.isBodyScrollLocked = false; }

  private blurActiveElement(): void { const activeElement = this.document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      activeElement !== this.document.body &&
      typeof activeElement.blur === 'function'
    ) { activeElement.blur(); } }

  private releaseFocusFromHiddenSidebar(): void { const sidebarElement = this.sidebarPanel?.nativeElement;
    const activeElement = this.document.activeElement;

    if (!sidebarElement || !(activeElement instanceof HTMLElement)) { return; }

    if (!sidebarElement.contains(activeElement)) { return; }

    if (typeof activeElement.blur === 'function') { activeElement.blur(); } } }
